import uuidV4 from 'lib/string/uuidV4Utils'
import { defineMessages } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { CHECKOUT_ITEM_TYPE_GIFT_CARD } from 'constants/checkout'
import config from 'constants/config'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import type { Wedding } from 'weddings/api/weddings'

const messages = defineMessages({
  weddingGiftCardDescription: {
    id: 'Az8CgWRMJp-_YAXZkmBD',
    defaultMessage: 'This gift card will be sent to {coupleName}. They can use it towards their next luxury escape.',
    description: 'Description shown on the gift card review step for a wedding gift card',
  },
  weddingCoupleName: {
    id: 'iwS2CYx5ma8W4pIZrgwy',
    defaultMessage: '{partnerOneName} & {partnerTwoName}',
    description: 'Display name for a wedding couple shown in the wedding gifting flows and share content',
  },
  weddingGiftCardProductName: {
    id: 'JCiRAXC470HDQKfEFLs2',
    defaultMessage: '{coupleName}’s Wedding',
    description: 'Product name shown for a wedding gift card in checkout and gift card redemption flows',
  },
  giftCardType: {
    id: 'Hg-840IJ9ZbnyLULyBB4',
    defaultMessage: 'gift card',
    description: 'Generic product type label shown in gift card redemption instructions and previews',
  },
  experienceType: {
    id: 'o9eZr7NvOz1e1wEwenT2',
    defaultMessage: 'experience',
    description: 'Generic product type label shown in gift card redemption instructions for experience gift cards',
  },
  experienceTicketCount: {
    id: 'Sp34xBrbLqg_Zw1LbXey',
    defaultMessage: '{ticketName} × {count}',
    description: 'Label showing an experience ticket name with the selected quantity in gift card product details',
  },
})

export function generateGiftCardItem(amount: number): App.Checkout.GiftCardItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_GIFT_CARD,
    amount,
    deliveryMethod: 'email',
    content: {},
  }
}

export interface WeddingGiftCardDonor {
  name: string
  message?: string
}

export function generateWeddingGiftCardItem(
  wedding: Wedding,
  amount: number,
  intl: IntlShape,
  donor?: WeddingGiftCardDonor,
): App.Checkout.GiftCardItem {
  const coupleName = intl.formatMessage(
    messages.weddingCoupleName,
    {
      partnerOneName: wedding.partnerOneFirstName,
      partnerTwoName: wedding.partnerTwoFirstName,
    },
  )

  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_GIFT_CARD,
    amount,
    deliveryMethod: 'email',
    content: {
      recipientName: coupleName,
      recipientEmail: wedding.giftNotificationEmail || undefined,
      fromName: donor?.name,
      message: donor?.message,
    },
    product: {
      image: { id: NO_IMAGE_AVAILABLE_ID, url: '' },
      name: intl.formatMessage(
        messages.weddingGiftCardProductName,
        {
          coupleName,
        },
      ),
      items: [],
      type: intl.formatMessage(messages.giftCardType),
      location: wedding.locationLabel ?? '',
      url: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/weddings/${wedding.slug}`,
      description: intl.formatMessage(messages.weddingGiftCardDescription, { coupleName }),
    },
  }
}

export function generateExperienceGiftCardItem(
  experience: App.ExperienceOffer,
  amount: number,
  intl: IntlShape,
  ticketCounts: Array<{ ticket: App.ExperienceItemTicket, count: number }>,
): App.Checkout.GiftCardItem {
  return {
    itemId: uuidV4(),
    transactionKey: uuidV4(),
    itemType: CHECKOUT_ITEM_TYPE_GIFT_CARD,
    amount,
    deliveryMethod: 'email',
    content: {},
    product: {
      image: experience.images[0],
      items: ticketCounts.map((ticketItem) => {
        if (ticketItem.count <= 1) {
          return ticketItem.ticket.name
        }

        return intl.formatMessage(
          messages.experienceTicketCount,
          {
            ticketName: ticketItem.ticket.name,
            count: ticketItem.count,
          },
        )
      }),
      name: experience.name,
      type: intl.formatMessage(messages.experienceType),
      location: experience.location.name,
      url: `${config.SCHEME}://${config.CUSTOMER_PORTAL_HOST}/experience/${experience.slug}/${experience.id}`,
    },
  }
}
